import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, useMediaQuery } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';

// Import responsive styles
import './styles/responsive.css';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import { NotificationProvider } from './context/NotificationContext';

// Layout Components
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import Footer from './components/Footer/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';
import PWAInstallPrompt from './components/PWA/PWAInstallPrompt';
import PWAUpdatePrompt from './components/PWA/PWAUpdatePrompt';

// Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import AccountDeleted from './pages/auth/AccountDeleted';
import Logout from './pages/auth/Logout';
import Profile from './pages/Profile/Profile';
import AccountSettings from './pages/account/AccountSettings';
import SeriesList from './pages/Series/SeriesList';
const SeriesDetail = React.lazy(() => import('./pages/Series/SeriesDetail'));

// Lazy load other pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const GamesList = React.lazy(() => import('./pages/Games/GamesList'));
const CreateGame = React.lazy(() => import('./pages/Games/CreateGame'));
const JoinGame = React.lazy(() => import('./pages/Games/JoinGame'));
const JoinGameByCode = React.lazy(() => import('./pages/Games/JoinGameByCode'));
const AllGames = React.lazy(() => import('./pages/Games/AllGames'));
const EditGameDetails = React.lazy(() => import('./pages/Games/EditGameDetails'));
const GameOwnerDetails = React.lazy(() => import('./pages/Game/GameOwnerDetails'));
const GameOwners = React.lazy(() => import('./pages/Game/GameOwners'));
const DetailedFantasyPoints = React.lazy(() => import('./pages/Game/DetailedFantasyPoints'));
const CreateSeries = React.lazy(() => import('./pages/Series/CreateSeries'));
const CurrentSeries = React.lazy(() => import('./pages/Series/CurrentSeries'));
const GameDetails = React.lazy(() => import('./pages/Games/GameDetails'));
const CreatePlayingXI = React.lazy(() => import('./pages/Games/CreatePlayingXI'));
const AuctionsList = React.lazy(() => import('./pages/Auction/AuctionsList'));
const AuctionRoom = React.lazy(() => import('./pages/Auction/AuctionRoom'));
const PlayersList = React.lazy(() => import('./pages/Players/PlayersList'));
const PlayerStats = React.lazy(() => import('./pages/Players/PlayerStats'));
const TeamsList = React.lazy(() => import('./pages/Teams/TeamsList'));
const OwnersList = React.lazy(() => import('./pages/Owners/OwnersList'));
const MatchesList = React.lazy(() => import('./pages/Matches/MatchesList'));
const AllMatches = React.lazy(() => import('./pages/Matches/AllMatches'));
const SeriesMatches = React.lazy(() => import('./pages/Matches/SeriesMatches'));
const CreateMatchContest = React.lazy(() => import('./pages/Contest/CreateMatchContest'));
const ContestsList = React.lazy(() => import('./pages/Contest/ContestsList'));
const ContestDetails = React.lazy(() => import('./pages/Contest/ContestDetails'));
const MyContests = React.lazy(() => import('./pages/Contest/MyContests'));
const ContestPlayingXI = React.lazy(() => import('./pages/Contest/ContestPlayingXI'));
const VenuesList = React.lazy(() => import('./pages/Venues/VenuesList'));
const AdminRouter = React.lazy(() => import('./pages/Admin/AdminRouter'));
const HomePage = React.lazy(() => import('./pages/Home/HomePage'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const TermsAndConditions = React.lazy(() => import('./pages/Legal/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/Legal/PrivacyPolicyFixed'));
const HelpCenter = React.lazy(() => import('./pages/Legal/HelpCenterSimple'));
const GameRules = React.lazy(() => import('./pages/Legal/GameRules'));
const FAQ = React.lazy(() => import('./pages/Legal/FAQ'));
const ContactUs = React.lazy(() => import('./pages/Legal/ContactUs'));
const Feedback = React.lazy(() => import('./pages/Legal/Feedback'));
const Disclaimer = React.lazy(() => import('./pages/Legal/Disclaimer'));
const CookiePolicy = React.lazy(() => import('./pages/Legal/CookiePolicyFixed'));

// Fallback loading component
const LazyLoadFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="300px"
  >
    Loading...
  </Box>
);

function App() {
  const [darkMode, setDarkMode] = useState(true); // Set default theme to dark
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark');

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#dc004e',
        light: '#ff5983',
        dark: '#9a0036',
      },
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 600,
        fontSize: 'clamp(2rem, 5vw, 3.5rem)',
      },
      h2: {
        fontWeight: 600,
        fontSize: 'clamp(1.75rem, 4vw, 3rem)',
      },
      h3: {
        fontWeight: 600,
        fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
      },
      h4: {
        fontWeight: 600,
        fontSize: 'clamp(1.25rem, 3vw, 2rem)',
      },
      h5: {
        fontWeight: 500,
        fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
      },
      h6: {
        fontWeight: 500,
        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      },
      body1: {
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      },
      body2: {
        fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            '@media (max-width: 600px)': {
              padding: '6px 12px',
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              paddingLeft: 8,
              paddingRight: 8,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            '@media (max-width: 900px)': {
              width: '100vw',
              maxWidth: '100vw',
            },
            '@media (min-width: 901px)': {
              width: 280,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '@media (max-width: 600px)': {
                fontSize: '0.875rem',
              },
            },
          },
        },
      },
    },
  });

  const handleThemeToggle = () => {
    setDarkMode(!darkMode);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <GameProvider>
          <Router>
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
              <Routes>
                {/* Public Routes (No Layout) */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/signup" element={<Signup />} />
                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/account-deleted" element={<AccountDeleted />} />
                <Route path="/auth/logout" element={<Logout />} />
                
                {/* Routes with Layout */}
                <Route
                  path="/*"
                  element={
                    <Box sx={{ 
                      display: 'flex', 
                      width: '100%', 
                      minHeight: '100vh',
                      flexDirection: { xs: 'column', md: 'row' },
                      overflow: 'hidden', // Prevent horizontal scrolling
                    }}>
                      <Header
                        onMenuClick={handleSidebarToggle}
                        onThemeToggle={handleThemeToggle}
                        isDarkMode={darkMode}
                      />
                      <Sidebar
                        open={sidebarOpen}
                        onClose={handleSidebarClose}
                        variant="temporary"
                      />
                      <Box
                        component="main"
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          p: { xs: 1, sm: 2, md: 3 },
                          overflow: 'hidden',
                          minWidth: 0, // Prevents flex item from overflowing
                        }}
                      >
                        <Box sx={{ 
                          mt: { xs: 7, md: 8 }, 
                          flex: 1,
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'auto',
                          minHeight: 0, // Prevents overflow
                        }}>
                          <React.Suspense fallback={<LazyLoadFallback />}>
                            <Routes>
                              {/* Public Routes */}
                              <Route path="/" element={<HomePage />} />
                              
                              {/* Protected Routes */}
                              <Route
                                path="/dashboard"
                                element={
                                  <ProtectedRoute>
                                    <Dashboard />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/profile"
                                element={
                                  <ProtectedRoute>
                                    <Profile />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/account/settings"
                                element={
                                  <ProtectedRoute>
                                    <AccountSettings />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Series Routes */}
                              <Route
                                path="/series/all"
                                element={
                                  <ProtectedRoute>
                                    <SeriesList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/series/:seriesId"
                                element={
                                  <ProtectedRoute>
                                    <SeriesDetail />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/series/current"
                                element={
                                  <ProtectedRoute>
                                    <CurrentSeries />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/series/create"
                                element={
                                  <ProtectedRoute requireAdmin={true}>
                                    <CreateSeries />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Game Routes */}
                              <Route
                                path="/games/my-games"
                                element={
                                  <ProtectedRoute>
                                    <GamesList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/games/create"
                                element={
                                  <ProtectedRoute>
                                    <CreateGame />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/games/join"
                                element={
                                  <ProtectedRoute>
                                    <JoinGame />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/join-game"
                                element={
                                  <ProtectedRoute>
                                    <JoinGameByCode />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/games/all"
                                element={
                                  <ProtectedRoute requireAdmin={true}>
                                    <AllGames />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/games/edit/:gameId"
                                element={
                                  <ProtectedRoute>
                                    <EditGameDetails />
                                  </ProtectedRoute>
                                }
                              />
              <Route
                path="/games/:gameId"
                element={
                  <ProtectedRoute>
                    <GameDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/games/:gameId/owners"
                element={
                  <ProtectedRoute>
                    <GameOwners />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/games/:gameId/create-playing-xi"
                element={
                  <ProtectedRoute>
                    <CreatePlayingXI />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/game/:gameId/detailed-fantasy-points"
                element={
                  <ProtectedRoute>
                    <DetailedFantasyPoints />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/series/:seriesId/game/:gameId/owner/:ownerId"
                element={
                  <ProtectedRoute>
                    <GameOwnerDetails />
                  </ProtectedRoute>
                }
              />                              {/* Auction Routes */}
                              <Route
                                path="/auctions/game/:gameId"
                                element={
                                  <ProtectedRoute>
                                    <AuctionsList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/auction/:auctionId"
                                element={
                                  <ProtectedRoute>
                                    <AuctionRoom />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Contest Routes */}
                              <Route
                                path="/contests/all"
                                element={
                                  <ProtectedRoute>
                                    <ContestsList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/contests/my-contests"
                                element={
                                  <ProtectedRoute>
                                    <MyContests />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Match Routes */}
                              <Route
                                path="/matches/all"
                                element={
                                  <ProtectedRoute>
                                    <AllMatches />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/matches/series/:seriesId"
                                element={
                                  <ProtectedRoute>
                                    <SeriesMatches />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/matches"
                                element={
                                  <ProtectedRoute>
                                    <AllMatches />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Contest Routes */}
                              <Route
                                path="/contest/create/:matchId"
                                element={
                                  <ProtectedRoute>
                                    <CreateMatchContest />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/contests/match/:matchId"
                                element={
                                  <ProtectedRoute>
                                    <ContestsList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/contest/:contestId/details"
                                element={
                                  <ProtectedRoute>
                                    <ContestDetails />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/contest/:contestId/playing-xi"
                                element={
                                  <ProtectedRoute>
                                    <ContestPlayingXI />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Venue Routes */}
                              <Route
                                path="/venues/all"
                                element={
                                  <ProtectedRoute>
                                    <VenuesList />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Player Routes */}
                              <Route
                                path="/players/all"
                                element={
                                  <ProtectedRoute>
                                    <PlayersList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/players/stats"
                                element={
                                  <ProtectedRoute>
                                    <PlayerStats />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Settings Route */}
                              <Route
                                path="/settings"
                                element={
                                  <ProtectedRoute>
                                    <Settings />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Legal Routes */}
                              <Route path="/terms" element={<TermsAndConditions />} />
                              <Route path="/privacy" element={<PrivacyPolicy />} />
                              <Route path="/help" element={<HelpCenter />} />
                              <Route path="/game-rules" element={<GameRules />} />
                              <Route path="/faq" element={<FAQ />} />
                              <Route path="/contact" element={<ContactUs />} />
                              <Route path="/feedback" element={<Feedback />} />
                              <Route path="/disclaimer" element={<Disclaimer />} />
                              <Route path="/cookies" element={<CookiePolicy />} />
                              
                              {/* Team Routes */}
                              <Route
                                path="/teams/all"
                                element={
                                  <ProtectedRoute>
                                    <TeamsList />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Admin Routes */}
                              <Route
                                path="/admin/*"
                                element={
                                  <ProtectedRoute requireAdmin={true}>
                                    <AdminRouter />
                                  </ProtectedRoute>
                                }
                              />
                              
                              {/* Redirect root to dashboard if authenticated, otherwise to home */}
                              <Route
                                path="/redirect"
                                element={<Navigate to="/dashboard" replace />}
                              />
                              
                              {/* Catch all route */}
                              <Route
                                path="*"
                                element={
                                  <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <h1>404 - Page Not Found</h1>
                                    <p>The page you are looking for does not exist.</p>
                                  </Box>
                                }
                              />
                            </Routes>
                          </React.Suspense>
                        </Box>
                        <Footer />
                      </Box>
                    </Box>
                  }
                />
              </Routes>
            </Box>
            
            {/* PWA Install Prompt */}
            <PWAInstallPrompt />
            
            {/* PWA Update Prompt */}
            <PWAUpdatePrompt />
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: darkMode ? '#333' : '#fff',
                  color: darkMode ? '#fff' : '#333',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: theme.palette.success.main,
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: theme.palette.error.main,
                    secondary: '#fff',
                  },
                },
              }}
            />
          </Router>
        </GameProvider>
        </NotificationProvider>
      </AuthProvider>
      <Analytics />
    </ThemeProvider>
  );
}

export default App;
